import {
  type GroupMember,
  type GroupRole,
  useLeaveGroup,
  useRemoveMember,
  useSetMemberRole,
  useSetMemberStatus,
  useTransferOwnership,
} from '@hobbytools/data';
import { Icon } from '@hobbytools/icons';
import { friendlyError } from '@hobbytools/platform';
import { Banner, Button, Card, ConfirmDialog, Menu, MenuItem } from '@hobbytools/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { GroupCampaigns } from './GroupCampaigns';
import { Invites } from './GroupInvites';
import { useGroupContext } from './GroupLayout';

const roleLabel: Record<GroupRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const rank: Record<GroupRole, number> = { owner: 3, admin: 2, member: 1 };

type Pending =
  | { kind: 'remove'; member: GroupMember }
  | { kind: 'transfer'; member: GroupMember }
  | { kind: 'leave' }
  | null;

export function GroupMembers() {
  const { group, members, me, userId, canManage } = useGroupContext();
  const navigate = useNavigate();
  const setRole = useSetMemberRole(group.id);
  const setStatus = useSetMemberStatus(group.id);
  const remove = useRemoveMember(group.id);
  const transfer = useTransferOwnership(group.id);
  const leave = useLeaveGroup(group.id);
  const [confirm, setConfirm] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);

  const myRank = me?.status === 'approved' ? rank[me.role] : 0;
  const outranks = (m: GroupMember) => myRank > rank[m.role];
  const waiting = members.filter((m) => m.status === 'pending');
  const roster = members.filter((m) => m.status === 'approved');
  const declined = members.filter((m) => m.status === 'denied');

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setError(null);
    try {
      await fn();
      after?.();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setConfirm(null);
    }
  };

  const confirmBusy = remove.isPending || transfer.isPending || leave.isPending;

  return (
    <div className="grid gap-6 md:grid-cols-[3fr_2fr]">
      <div className="flex flex-col gap-6">
        {error ? <Banner tone="danger">{error}</Banner> : null}

        {canManage && waiting.length > 0 ? (
          <Card className="flex flex-col gap-3">
            <h2 className="heading-display text-xl">Waiting for approval</h2>
            <ul className="flex flex-col divide-y divide-border">
              {waiting.map((m) => (
                <li key={m.userId} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <span>{m.displayName}</span>
                  <span className="flex gap-2">
                    <Button
                      size="sm"
                      onPress={() =>
                        run(() => setStatus.mutateAsync({ userId: m.userId, status: 'approved' }))
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onPress={() => run(() => setStatus.mutateAsync({ userId: m.userId, status: 'denied' }))}
                    >
                      Decline
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card className="flex flex-col gap-3">
          <h2 className="heading-display text-xl">Members</h2>
          <ul className="flex flex-col divide-y divide-border">
            {(me?.status === 'approved' ? roster : members).map((m) => (
              <li
                key={m.userId}
                className="flex items-center gap-3 py-2"
                data-testid={`member-${m.displayName}`}
              >
                <span className="min-w-0 flex-1 truncate">
                  {m.displayName}
                  {m.userId === userId ? <span className="text-fg-muted"> (you)</span> : null}
                </span>
                <span className="text-sm text-fg-muted">
                  {m.status === 'pending' ? 'Waiting for approval' : roleLabel[m.role]}
                </span>
                {m.status === 'approved' && m.userId !== userId && outranks(m) ? (
                  <Menu
                    trigger={
                      <Button variant="ghost" size="sm" iconOnly aria-label={`Actions for ${m.displayName}`}>
                        <Icon name="more-horizontal" size={20} />
                      </Button>
                    }
                  >
                    {me?.role === 'owner' && m.role === 'member' ? (
                      <MenuItem
                        onAction={() => run(() => setRole.mutateAsync({ userId: m.userId, role: 'admin' }))}
                      >
                        Make admin
                      </MenuItem>
                    ) : null}
                    {me?.role === 'owner' && m.role === 'admin' ? (
                      <MenuItem
                        onAction={() => run(() => setRole.mutateAsync({ userId: m.userId, role: 'member' }))}
                      >
                        Make member
                      </MenuItem>
                    ) : null}
                    {me?.role === 'owner' ? (
                      <MenuItem onAction={() => setConfirm({ kind: 'transfer', member: m })}>
                        Transfer ownership…
                      </MenuItem>
                    ) : null}
                    <MenuItem tone="danger" onAction={() => setConfirm({ kind: 'remove', member: m })}>
                      Remove from group…
                    </MenuItem>
                  </Menu>
                ) : (
                  <span className="w-9" aria-hidden="true" />
                )}
              </li>
            ))}
          </ul>

          {me && me.role !== 'owner' ? (
            <div className="border-t border-border pt-3">
              <Button variant="ghost" size="sm" onPress={() => setConfirm({ kind: 'leave' })}>
                Leave group…
              </Button>
            </div>
          ) : me?.role === 'owner' ? (
            <p className="border-t border-border pt-3 text-sm text-fg-muted">
              You own this group. To leave, transfer ownership to another member first.
            </p>
          ) : null}
        </Card>

        {canManage && declined.length > 0 ? (
          <Card className="flex flex-col gap-3">
            <h2 className="heading-display text-lg">Declined</h2>
            <ul className="flex flex-col divide-y divide-border">
              {declined.map((m) => (
                <li key={m.userId} className="flex items-center justify-between gap-3 py-2">
                  <span>{m.displayName}</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onPress={() => run(() => setStatus.mutateAsync({ userId: m.userId, status: 'approved' }))}
                  >
                    Approve after all
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>

      <div className="flex flex-col gap-6">
        {me?.status === 'approved' ? <GroupCampaigns groupId={group.id} canCreate={canManage} /> : null}
        {canManage ? <Invites groupId={group.id} userId={userId} /> : null}
      </div>

      <ConfirmDialog
        isOpen={confirm?.kind === 'remove'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm?.kind === 'remove' ? `Remove ${confirm.member.displayName}?` : ''}
        confirmLabel="Remove"
        tone="danger"
        isPending={confirmBusy}
        onConfirm={() => confirm?.kind === 'remove' && run(() => remove.mutateAsync(confirm.member.userId))}
      >
        They lose access to {group.name}. They can rejoin later with a new invite.
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirm?.kind === 'transfer'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm?.kind === 'transfer' ? `Make ${confirm.member.displayName} the owner?` : ''}
        confirmLabel="Transfer ownership"
        isPending={confirmBusy}
        onConfirm={() =>
          confirm?.kind === 'transfer' && run(() => transfer.mutateAsync(confirm.member.userId))
        }
      >
        They become the owner of {group.name} and you become an admin. Only they can transfer it back.
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirm?.kind === 'leave'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={`Leave ${group.name}?`}
        confirmLabel="Leave group"
        tone="danger"
        isPending={confirmBusy}
        onConfirm={() =>
          run(
            () => leave.mutateAsync(),
            () => navigate('/', { replace: true }),
          )
        }
      >
        You lose access to this group. You can rejoin later with an invite.
      </ConfirmDialog>
    </div>
  );
}
